import { formatDayMonth, formatMoney, t } from "@hakili/i18n";
import { BANK_FS_BODY, BANK_FS_META, BANK_TEXT_2, Callout, Chip } from "@hakili/ui";
import type { ReactElement } from "react";

import { COMMITTEE_INFO_ICON, type CommitteeRead, dayLabel, reasonSentence } from "./comite.js";
import { fill } from "./dossiers.js";

/**
 * `COMITE-RECORD-3` (D-1024-1, D-1024-7; frames 43 and 44; `P2-BKW-003`, WF-BNK-14's analyst side): **the analyst's committee stage waits, and says
 * so.** The analyst no longer prepares the committee (put aside with « Comité », D-1024-1a): the chef records the outcome himself
 * (`COMITE-RECORD-2`), so her view is one information bar and no action (« Aucune action de votre part à cette étape »), the three ways out of the
 * stage, and, after an ajournement, the card « Le comité a ajourné ce dossier ». The promise is TRUE (D2's « vous serez prévenu(e) » is worded « une notification vous le dira », the catalogue refusing a parenthesised agreement, 06 §6.5.5): the chef's
 * recording tells the file's analyst through the notifications module (`comite-enregistre`, `COMITE-RECORD-1`).
 *
 * Everything here is what the offer read sends (`committee: { reasons, sittings }`): the reasons kept at the buyer's acceptance, the sittings in the
 * order they were recorded. The screen compares nothing and invents no reason; a file with none kept says where it is and that nothing is asked.
 */
/** The offer the buyer accepted, as the offer read carries it: the amount lent, the duration, the nominal rate. */
export interface AcceptedOffer {
  readonly totalFinancedAmount: number;
  readonly durationMonths: number;
  readonly nominalRatePct: string;
}

const NBSP = "\u00a0";

/** The day the server stamped, in words by the shared formatter (« 22 octobre »); the numeric day only if the server sent no day. */
const dayInWords = (iso: string): string => {
  const instant = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(instant.getTime()) ? dayLabel(iso) : formatDayMonth(instant);
};

export function CommitteeWait(props: {
  readonly committee: CommitteeRead | null;
  readonly offer: AcceptedOffer | null;
}): ReactElement {
  const reasons = props.committee?.reasons ?? [];
  const sittings = props.committee?.sittings ?? [];
  const last = sittings[sittings.length - 1] ?? null;
  const adjourned = last !== null && last.outcome === "ajourne" ? last : null;
  const why = reasons
    .map((reason) =>
      reason.kind === "amount"
        ? fill("bank.comiteAttente.phraseMontant", {
            montant: formatMoney(reason.amountFinanced),
            seuil: formatMoney(reason.thresholdAmount),
          })
        : reasonSentence(reason),
    )
    .join(" ");
  return (
    <div data-testid="bank-comite-attente-page">
      <Callout tone="info" testId="bank-comite-attente" className="mb-4" icon={COMMITTEE_INFO_ICON}>
        <strong>{t("bank.comiteAttente.titre")}</strong> {why === "" ? "" : `${why} `}
        {t("bank.comiteAttente.fin")}
      </Callout>
      {adjourned === null ? null : (
        <div
          className="mb-4 rounded-2xl border border-gray-200 bg-white p-5"
          data-testid="bank-comite-ajourne-carte"
        >
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="m-0 text-base font-semibold">{t("bank.comiteAttente.ajourneTitre")}</h2>
              <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comiteAttente.ajourneSous")}</p>
            </div>
            <span data-kind="chip-label">
              <Chip tone="attention">{t("bank.comite.ajourne")}</Chip>
            </span>
          </div>
          <dl className="m-0">
            {(
              [
                [t("bank.comite.decisionSeance"), dayLabel(adjourned.sittingDate)],
                [t("bank.comiteAttente.issue"), t("bank.comite.ajourne")],
                [t("bank.comite.resume"), adjourned.summary],
                [
                  t("bank.comiteAttente.enregistrePar"),
                  `${adjourned.recordedBy} · ${dayInWords(adjourned.recordedOn)}`,
                ],
              ] as const
            ).map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2.5 last:border-b-0"
              >
                <dt className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>{label}</dt>
                <dd className="m-0 text-right text-sm font-medium text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      <div className="mb-4 grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] items-start gap-4">
        {props.offer === null ? null : (
          <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="bank-comite-offre">
            <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="m-0 text-base font-semibold">{t("bank.comiteAttente.offreTitre")}</h2>
              <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comiteAttente.offreSous")}</p>
            </div>
            <dl className="m-0">
              {(
                [
                  [t("bank.comite.montant"), formatMoney(props.offer.totalFinancedAmount), true],
                  [
                    t("bank.comite.duree"),
                    fill("bank.comite.dureeMois", { n: String(props.offer.durationMonths) }),
                    false,
                  ],
                  [t("bank.comite.taux"), `${props.offer.nominalRatePct.replace(".", ",")}${NBSP}%`, true],
                ] as const
              ).map(([label, value, mono]) => (
                <div
                  key={label}
                  className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2.5 last:border-b-0"
                >
                  <dt className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>{label}</dt>
                  <dd
                    className={`m-0 text-right text-sm font-medium tabular-nums text-gray-900${mono ? " font-mono" : ""}`}
                  >
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="bank-comite-devenir">
          <h2 className="m-0 mb-3 text-base font-semibold">{t("bank.comiteAttente.devenirTitre")}</h2>
          <ul className={`m-0 list-disc space-y-1.5 pl-5 ${BANK_FS_BODY} text-gray-700`}>
            <li>{t("bank.comiteAttente.devenirAccorde")}</li>
            <li>{t("bank.comiteAttente.devenirRefuse")}</li>
            <li>{t("bank.comiteAttente.devenirAjourne")}</li>
          </ul>
          <span className={`sr-only ${BANK_FS_META}`} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
